import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

// Three excerpts — one per flagship world. Each is a curated taste of the writing tone.
const EXCERPTS = [
  {
    world: "Aethelgard: The Shattered Crown",
    genre: "Dark High Fantasy LitRPG",
    accent: "text-amber-300",
    body: [
      "Rain hammers the flagstones of Grey Vigil. You draw breath — cold, iron-tasting, edged with old smoke.",
      "A hooded rider approaches, black-wax seal at his belt. House Corven's mark. The gate warden lowers his crossbow, then his eyes.",
      "'The Blooded ask for you by oath-name,' the rider says. He does not dismount. 'They have found something in the deep wells that speaks with a dead god's tongue.'",
    ],
  },
  {
    world: "Ash & Neon: Sector 9",
    genre: "Gritty Cyberpunk Post-Apocalyptic",
    accent: "text-cyan-300",
    body: [
      "Ash falls like slow snow across Neon Row. Your respirator hisses, filter clogged to hell.",
      "Below, the Corp convoy grinds through flooded arteries — six drones, one command-crawler, running dark. The cracked earpiece coughs static, then a voice:",
      "'Contract's live, wire-jockey. Twelve hundred credits to steal a memory that isn't yours. You in or out?'",
    ],
  },
  {
    world: "Eldritch Echoes: Sunken Citadel",
    genre: "Gothic Eldritch Mystery",
    accent: "text-teal-300",
    body: [
      "The pressure valves scream. Water — black, warm, wrong — presses the bronze walls of the bell.",
      "Below, the stones of Var-Illun glow. Not with candlelight. With something older, patient, aware.",
      "Somewhere in the flooded chapel, a voice is humming your childhood lullaby, note-perfect. You have not thought of that song in twenty years.",
    ],
  },
];

const REVEAL_MS_PER_WORD = 55;
const HOLD_AFTER_REVEAL_MS = 5000;

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [visibleWords, setVisibleWords] = useState(0);
  const [fadeState, setFadeState] = useState("in"); // in | hold | out
  const [mode, setMode] = useState("choose"); // "choose" | "signin" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    if (!loading && user) navigate("/app");
  }, [loading, user, navigate]);

  const excerpt = EXCERPTS[index];
  const words = useMemo(() => excerpt.body.join(" \n\n ").split(/(\s+)/), [excerpt]);
  const totalWords = words.length;

  // Typewriter reveal + rotate
  useEffect(() => {
    // reset on excerpt change
    setVisibleWords(0);
    setFadeState("in");
    timers.current.forEach(clearTimeout);
    timers.current = [];

    let step = 0;
    const revealNext = () => {
      step += 1;
      if (step >= totalWords) {
        setVisibleWords(totalWords);
        const holdT = setTimeout(() => {
          setFadeState("out");
          const swapT = setTimeout(() => {
            setIndex((i) => (i + 1) % EXCERPTS.length);
          }, 900);
          timers.current.push(swapT);
        }, HOLD_AFTER_REVEAL_MS);
        timers.current.push(holdT);
        return;
      }
      setVisibleWords(step);
      const nextChar = (words[step] || "").trim();
      const isPunct = /[.!?…—]$/.test(nextChar);
      const isPause = /[,;:]$/.test(nextChar);
      const isWs = /^\s+$/.test(words[step] || "");
      const wait = (isWs ? 0 : REVEAL_MS_PER_WORD) + (isPunct ? 260 : isPause ? 110 : 0);
      const t = setTimeout(revealNext, wait);
      timers.current.push(t);
    };
    const seed = setTimeout(revealNext, 400);
    timers.current.push(seed);
    return () => timers.current.forEach(clearTimeout);
  }, [index, totalWords, words]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const formatError = (detail) => {
    if (!detail) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
  };

  const submitEmailAuth = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const path = mode === "signup" ? "/auth/register" : "/auth/login";
      const payload = mode === "signup" ? { email, password, name } : { email, password };
      const { data } = await api.post(path, payload);
      setUser(data);
      navigate("/app");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const revealed = words.slice(0, visibleWords).join("");
  const paragraphs = revealed.split(/\n\n+/);

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row relative overflow-hidden" data-testid="login-page">
      {/* Left: Story Sample */}
      <section
        data-testid="story-sample"
        className="relative flex-1 lg:w-3/5 lg:min-h-screen px-6 sm:px-12 lg:px-20 py-10 sm:py-14 lg:py-24 flex flex-col justify-between gap-6 overflow-hidden order-last lg:order-none"
      >
        {/* atmospheric wash */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse at 20% 30%, rgba(212,175,55,0.09), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(153,27,27,0.08), transparent 60%)"
        }} />
        <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-amber-500/25 to-transparent hidden lg:block" />

        <div className="relative z-10 flex items-center gap-3">
          <span className="gold text-2xl leading-none">✦</span>
          <div className="font-mono text-[0.65rem] uppercase tracking-[0.45em] text-stone-500">
            An Excerpt
          </div>
        </div>

        <div className="relative z-10 max-w-2xl">
          <div
            data-testid="excerpt-body"
            className={`transition-opacity duration-700 ${fadeState === "out" ? "opacity-0" : "opacity-100"}`}
          >
            <div className={`font-mono text-[0.65rem] uppercase tracking-[0.3em] mb-4 ${excerpt.accent}`}>
              {excerpt.genre}
            </div>
            <div className="font-prose text-stone-200 text-lg sm:text-xl lg:text-2xl leading-relaxed space-y-5 prose-tome min-h-[220px] sm:min-h-[320px]">
              {paragraphs.map((p, i) => (
                <p key={i} className={i === 0 ? "dropcap" : ""}>
                  {p}
                  {i === paragraphs.length - 1 && visibleWords < totalWords && (
                    <span className="inline-block w-[0.35em] h-[1em] align-[-0.15em] ml-0.5 bg-amber-500 animate-pulse" aria-hidden="true" />
                  )}
                </p>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-3 font-mono text-[0.6rem] uppercase tracking-[0.35em] text-stone-500">
              <span className="h-px w-8 bg-amber-500/40" />
              <span>from {excerpt.world}</span>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-2" data-testid="excerpt-dots">
          {EXCERPTS.map((_, i) => (
            <button
              key={i}
              data-testid={`excerpt-dot-${i}`}
              onClick={() => setIndex(i)}
              aria-label={`Excerpt ${i + 1}`}
              className="py-4 px-1 -my-3 group"
            >
              <span className={`block h-1 rounded-sm transition-all ${i === index ? "w-8 bg-amber-500" : "w-3 bg-stone-700 group-hover:bg-stone-500"}`} />
            </button>
          ))}
          <div className="ml-3 font-mono text-[0.55rem] uppercase tracking-[0.35em] text-stone-600">
            {index + 1} of {EXCERPTS.length}
          </div>
        </div>
      </section>

      {/* Right: Sign-in */}
      <section
        data-testid="signin-panel"
        className="relative lg:w-2/5 lg:min-w-[440px] bg-stone-950/50 backdrop-blur-sm px-6 sm:px-12 py-10 sm:py-14 lg:py-24 flex flex-col justify-center border-b border-amber-500/15 lg:border-b-0 lg:border-l order-first lg:order-none"
      >
        <div className="max-w-md mx-auto lg:mx-0 space-y-10 flex-1 flex flex-col justify-center">
          <div>
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.45em] gold mb-3">Volume I · MMXXVI</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight">
              <span className="gold">Mythic</span><span className="text-stone-100">matrix</span>
            </h1>
            <div className="font-serif-body italic text-xl text-stone-300 mt-3">
              An engine of handcrafted worlds
            </div>
          </div>

          <div className="space-y-3 font-prose text-stone-300 leading-relaxed border-l border-amber-500/25 pl-4">
            <p>Twenty worlds. Strict rules that bend but never break.</p>
            <p>A game master forged from Claude Sonnet 5. Quality over quantity. Made for the reader fatigued by AI that folds to every whim.</p>
          </div>

          <ul className="space-y-2.5 font-serif-body text-stone-200 text-[0.95rem]">
            <FeatureLi>Handcrafted worlds with adaptive rules</FeatureLi>
            <FeatureLi>Hard Mode with unavoidable encounters & permadeath</FeatureLi>
            <FeatureLi>Community worlds crafted by other Sovereigns</FeatureLi>
          </ul>

          <div className="space-y-3">
            {mode === "choose" && (
              <>
                <button
                  data-testid="login-google-btn"
                  onClick={handleLogin}
                  className="btn-gold w-full sm:w-auto min-w-[280px]"
                >
                  Enter the Vault · Sign in with Google
                </button>
                <div className="flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-amber-500/15" />
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-stone-600">or</span>
                  <div className="flex-1 h-px bg-amber-500/15" />
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="show-signin-btn"
                    onClick={() => setMode("signin")}
                    className="btn-ghost flex-1 min-w-[130px]"
                  >
                    Sign in
                  </button>
                  <button
                    data-testid="show-signup-btn"
                    onClick={() => setMode("signup")}
                    className="btn-ghost flex-1 min-w-[130px]"
                  >
                    Create account
                  </button>
                </div>
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.35em] text-stone-500">
                  One-click · No credit card · Free tier included
                </div>
              </>
            )}

            {(mode === "signin" || mode === "signup") && (
              <form onSubmit={submitEmailAuth} className="space-y-3" data-testid={`${mode}-form`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[0.6rem] uppercase tracking-[0.3em] gold">
                    {mode === "signin" ? "Sign in with email" : "Create your account"}
                  </div>
                  <button type="button" onClick={() => setMode("choose")} className="font-mono text-[0.6rem] uppercase tracking-widest text-stone-500 hover:text-stone-300" data-testid="back-to-choose">
                    ← back
                  </button>
                </div>
                {mode === "signup" && (
                  <input
                    data-testid="name-input"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    minLength={1}
                    maxLength={80}
                    className="w-full bg-stone-900/80 border border-amber-500/25 focus:border-amber-500 outline-none px-3 py-2 rounded-sm font-prose text-stone-200 placeholder:text-stone-600"
                  />
                )}
                <input
                  data-testid="email-input"
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-stone-900/80 border border-amber-500/25 focus:border-amber-500 outline-none px-3 py-2 rounded-sm font-prose text-stone-200 placeholder:text-stone-600"
                />
                <input
                  data-testid="password-input"
                  type="password"
                  autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  placeholder={mode === "signin" ? "Password" : "Password (min 8 characters)"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={mode === "signup" ? 8 : 1}
                  className="w-full bg-stone-900/80 border border-amber-500/25 focus:border-amber-500 outline-none px-3 py-2 rounded-sm font-prose text-stone-200 placeholder:text-stone-600"
                />
                <button
                  type="submit"
                  data-testid="submit-email-auth-btn"
                  disabled={submitting}
                  className="btn-gold w-full"
                >
                  {submittingLabel(submitting, mode)}
                </button>
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-stone-500 text-center">
                  {mode === "signin" ? (
                    <>
                      No account?{" "}
                      <button type="button" onClick={() => setMode("signup")} className="gold hover:underline" data-testid="switch-to-signup">
                        Create one
                      </button>
                    </>
                  ) : (
                    <>
                      Already a Sovereign?{" "}
                      <button type="button" onClick={() => setMode("signin")} className="gold hover:underline" data-testid="switch-to-signin">
                        Sign in
                      </button>
                    </>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>

        <footer className="mt-10 pt-5 border-t border-amber-500/10 max-w-md mx-auto lg:mx-0 flex items-center justify-between flex-wrap gap-x-4 gap-y-2 font-mono text-[0.6rem] uppercase tracking-[0.3em] text-stone-500">
          <a href="/founders" data-testid="footer-founders-link" className="hover:gold transition-colors inline-block py-3">✦ The Founders' Wall</a>
          <div className="flex items-center gap-4">
            <a href="/privacy" data-testid="footer-privacy-link" className="hover:gold transition-colors">Privacy</a>
            <a href="/terms" data-testid="footer-terms-link" className="hover:gold transition-colors">Terms</a>
            <span>Mythicmatrix · MMXXVI</span>
          </div>
        </footer>
      </section>
    </div>
  );
}

function FeatureLi({ children }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="gold mt-1 leading-none">◆</span>
      <span>{children}</span>
    </li>
  );
}

function submittingLabel(submitting, mode) {
  if (submitting) return "...";
  if (mode === "signin") return "Sign in";
  return "Create account";
}
